import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import { useCambiarEstadoPedido, useFacturas, usePedidos } from '../api/queries';
import { useLocalOrders } from '../offline/hooks';
import { localOrderToPedido } from '../offline/outbox';
import { useToast } from '../ui/ToastProvider';
import { api } from '../api/client';
import type { Factura, Pedido } from '../api/types';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  EyeIcon,
  SearchIcon,
  XIcon,
} from './icons';
import { DataNotice, StatusBadge, VolumeBadge } from './Badges';
import { formatDate, formatMoney, formatTime } from '../lib/format';

const ESTADOS_PEDIDO = [
  { value: '', label: 'Todas' },
  { value: 'procesada', label: 'Procesadas' },
  { value: 'enviada', label: 'Enviadas' },
  { value: 'rechazada', label: 'Rechazadas' },
];

const ESTADOS_FACTURA = [
  { value: '', label: 'Todas' },
  { value: 'procesada', label: 'Procesadas' },
  { value: 'emitida', label: 'Emitidas' },
  { value: 'anulada', label: 'Anuladas' },
];

interface TransactionsTableProps {
  full?: boolean;
  initialSearch?: string;
}

export function TransactionsTable({ full = false, initialSearch = '' }: TransactionsTableProps) {
  const { demo, user } = useAuth();
  const { formatMoney: displayMoney } = useCurrency();
  const { notify } = useToast();
  const [view, setView] = useState<'pedidos' | 'facturas'>('pedidos');
  const [estado, setEstado] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Pedido | null>(null);

  useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  useEffect(() => {
    setPage(1);
  }, [view, estado, desde, hasta, search]);

  const limit = full ? 20 : 8;
  const pedidos = usePedidos({
    estado: estado || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
    search: search || undefined,
    page,
    limit,
  });
  const facturas = useFacturas({
    estado: estado || undefined,
    search: search || undefined,
    page,
    limit,
  });

  const localOrders = useLocalOrders();

  const pedidoRows = useMemo(() => {
    const serverRows = pedidos.data?.data ?? [];
    if (page !== 1) return serverRows;
    const serverIds = new Set(serverRows.map((row) => row.id));
    const locals = localOrders
      .filter((order) => !order.serverId || !serverIds.has(order.serverId))
      .map(localOrderToPedido)
      .filter((order) => {
        if (estado && order.estado !== estado) return false;
        const fecha = order.createdAt.slice(0, 10);
        if (desde && fecha < desde) return false;
        if (hasta && fecha > hasta) return false;
        if (search) {
          const term = search.toLowerCase();
          if (
            !order.numero.toLowerCase().includes(term) &&
            !order.clienteNombre.toLowerCase().includes(term) &&
            !order.clienteRif.toLowerCase().includes(term)
          ) {
            return false;
          }
        }
        return true;
      });
    return [...locals, ...serverRows];
  }, [pedidos.data, localOrders, page, estado, desde, hasta, search]);

  const facturaRows = facturas.data?.data ?? [];
  const rows = view === 'pedidos' ? pedidoRows : facturaRows;
  const activeQuery = view === 'pedidos' ? pedidos : facturas;
  const notice = view === 'pedidos' ? pedidos.data : facturas.data;
  const tabs = view === 'pedidos' ? ESTADOS_PEDIDO : ESTADOS_FACTURA;

  const total = activeQuery.data?.meta.total ?? 0;
  const pages = activeQuery.data?.meta.pages ?? 1;

  return (
    <section className="card transactions-card" aria-labelledby="transactions-title">
      <div className="transactions-head">
        <div className="segmented segmented--solid" role="tablist" aria-label="Tipo de documento">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'pedidos'}
            className={view === 'pedidos' ? 'is-active' : undefined}
            onClick={() => {
              setView('pedidos');
              setEstado('');
            }}
          >
            Pedidos & Cotizaciones
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'facturas'}
            className={view === 'facturas' ? 'is-active' : undefined}
            onClick={() => {
              setView('facturas');
              setEstado('');
            }}
          >
            Facturas Comerciales
          </button>
        </div>
        {full ? null : (
          <Link className="text-link" to="/pedidos">
            Ver todos los movimientos →
          </Link>
        )}
      </div>

      <div className="transactions-filters">
        <div className="filter-tabs" role="tablist" aria-label="Filtrar por estado">
          {tabs.map((tab) => (
            <button
              key={tab.value || 'todas'}
              type="button"
              role="tab"
              aria-selected={estado === tab.value}
              className={estado === tab.value ? 'is-active' : undefined}
              onClick={() => setEstado(tab.value)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="filter-controls">
          <label className="date-range">
            <CalendarIcon size={16} />
            <input
              type="date"
              value={desde}
              onChange={(event) => setDesde(event.target.value)}
              aria-label="Fecha desde"
            />
            <span>—</span>
            <input
              type="date"
              value={hasta}
              onChange={(event) => setHasta(event.target.value)}
              aria-label="Fecha hasta"
            />
          </label>
          <div className="search-input search-input--sm">
            <SearchIcon size={16} />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="RIF / cliente / documento"
              aria-label="Buscar movimientos"
            />
          </div>
        </div>
      </div>

      <DataNotice
        demo={notice?.demo}
        offline={notice?.offline}
        message={
          notice?.demo
            ? 'Datos de demostración · los pedidos nuevos aparecen desde la cola local.'
            : undefined
        }
      />

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Documento</th>
              <th scope="col">Cliente / RIF</th>
              <th scope="col" className="col-vendor">
                Vendedor
              </th>
              <th scope="col">Volumen</th>
              <th scope="col" className="align-right">
                Total
              </th>
              <th scope="col">Estado</th>
              <th scope="col">
                <span className="sr-only">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {activeQuery.isLoading ? (
              <tr>
                <td colSpan={7}>
                  <div className="table-empty">Cargando movimientos…</div>
                </td>
              </tr>
            ) : null}

            {!activeQuery.isLoading && activeQuery.isError ? (
              <tr>
                <td colSpan={7}>
                  <div className="table-empty">
                    <p>No se pudieron cargar los movimientos.</p>
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => void activeQuery.refetch()}
                    >
                      Reintentar
                    </button>
                  </div>
                </td>
              </tr>
            ) : null}

            {!activeQuery.isLoading && !activeQuery.isError && rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="table-empty">
                    <p>Sin movimientos en este rango.</p>
                    <span>Ajusta los filtros o registra un pedido nuevo.</span>
                  </div>
                </td>
              </tr>
            ) : null}

            {view === 'pedidos'
              ? pedidoRows.map((pedido) => (
                  <tr key={`${pedido.id}-${pedido.numero}`} className={pedido.local ? 'row--local' : undefined}>
                    <td>
                      <div className="doc-cell">
                        <strong>{pedido.numero}</strong>
                        <span>
                          {formatDate(pedido.createdAt)} · {formatTime(pedido.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="doc-cell">
                        <strong>{pedido.clienteNombre}</strong>
                        <span>{pedido.clienteRif}</span>
                      </div>
                    </td>
                    <td className="col-vendor">{pedido.vendedorNombre}</td>
                    <td>
                      <VolumeBadge cajas={pedido.cajas} />
                    </td>
                    <td className="align-right">
                      <div className="amount-cell">
                        <strong>
                          {pedido.moneda === 'VES'
                            ? formatMoney(pedido.totalMoneda, 'VES')
                            : displayMoney(pedido.totalUsd)}
                        </strong>
                        {pedido.moneda !== 'VES' ? (
                          <span>{displayMoney(pedido.totalUsd)}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <StatusBadge
                        estado={pedido.estado}
                        local={pedido.local}
                        syncStatus={
                          pedido.syncStatus ??
                          (pedido.origen === 'offline' && pedido.estado === 'en_cola'
                            ? 'synced'
                            : undefined)
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="icon-button icon-button--tiny"
                        aria-label={`Ver detalle de ${pedido.numero}`}
                        onClick={() => setSelected(pedido)}
                      >
                        <EyeIcon size={17} />
                      </button>
                    </td>
                  </tr>
                ))
              : facturaRows.map((factura: Factura) => (
                  <tr key={factura.id}>
                    <td>
                      <div className="doc-cell">
                        <strong>{factura.numero}</strong>
                        <span>{formatDate(factura.fechaEmision)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="doc-cell">
                        <strong>{factura.clienteNombre}</strong>
                        <span>{factura.clienteRif}</span>
                      </div>
                    </td>
                    <td className="col-vendor">{factura.vendedorNombre}</td>
                    <td>
                      <span className="volume-badge volume-badge--muted">—</span>
                    </td>
                    <td className="align-right">
                      <div className="amount-cell">
                        <strong>{formatMoney(factura.totalVes, 'VES')}</strong>
                        <span>Saldo {formatMoney(factura.saldoVes, 'VES')}</span>
                      </div>
                    </td>
                    <td>
                      <StatusBadge estado={factura.estado} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="icon-button icon-button--tiny"
                        aria-label={`Ver factura ${factura.numero}`}
                        onClick={() =>
                          notify(
                            `Factura ${factura.numero} · ${factura.clienteNombre} · ${displayMoney(factura.totalUsd)}`,
                            'info',
                          )
                        }
                      >
                        <EyeIcon size={17} />
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <footer className="table-footer">
        <span>
          Mostrando {rows.length === 0 ? 0 : (page - 1) * limit + 1}–
          {Math.min(page * limit, Math.max(total, rows.length))} de {Math.max(total, rows.length)} movimientos
        </span>
        {full ? (
          <div className="pager">
            <button
              type="button"
              className="btn btn--outline btn--sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Anterior
            </button>
            <span className="pager__current">
              {page} / {pages}
            </span>
            <button
              type="button"
              className="btn btn--outline btn--sm"
              disabled={page >= pages}
              onClick={() => setPage((current) => current + 1)}
            >
              Siguiente
            </button>
          </div>
        ) : (
          <Link className="text-link" to="/pedidos">
            Ver todos
          </Link>
        )}
      </footer>

      {selected ? (
        <PedidoDetalle pedido={selected} onClose={() => setSelected(null)} />
      ) : null}
    </section>
  );
}

function PedidoDetalle({ pedido, onClose }: { pedido: Pedido; onClose: () => void }) {
  const { demo, user } = useAuth();
  const { formatMoney: displayMoney } = useCurrency();
  const { notify } = useToast();
  const localOrders = useLocalOrders();
  const change = useCambiarEstadoPedido();
  const local = localOrders.find(
    (order) => order.id === pedido.id || order.serverId === pedido.id,
  );

  const detail = useQuery({
    queryKey: ['pedido-detalle', pedido.id],
    enabled: !pedido.local && !demo,
    queryFn: () => api.pedidos.detail(pedido.id),
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const items = useMemo(() => {
    if (pedido.local) {
      return (local?.items ?? []).map((item) => ({
        id: item.productoId,
        sku: item.sku,
        nombre: item.nombre,
        cantidad: item.cantidad,
        totalUsd:
          Math.round(
            item.precioUnitarioUsd * item.cantidad * (1 + item.impuestoPct / 100) * 100,
          ) / 100,
      }));
    }
    return detail.data?.data.items ?? [];
  }, [pedido.local, local, detail.data]);
  const puedeProcesar = user?.rol === 'admin' || user?.rol === 'supervisor';
  const next =
    pedido.estado === 'en_cola' || pedido.estado === 'rechazada'
      ? { estado: 'enviada', label: 'Marcar como enviada' }
      : pedido.estado === 'enviada' && puedeProcesar
        ? { estado: 'procesada', label: 'Marcar como procesada' }
        : null;

  const advance = async () => {
    if (!next) return;
    try {
      await change.mutateAsync({ id: pedido.id, estado: next.estado });
      notify(`Pedido ${pedido.numero} actualizado a ${next.label.toLowerCase()}.`, 'success');
      onClose();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo actualizar el pedido.', 'error');
    }
  };

  return createPortal(
    <div
      className="modal-backdrop modal-backdrop--center"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="order-title">
        <header className="modal-card__head">
          <div>
            <p className="eyebrow">Detalle del pedido</p>
            <h2 id="order-title">{pedido.numero}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Cerrar detalle">
            <XIcon size={20} />
          </button>
        </header>

        <div className="modal-card__body">
          <div className="detail-grid">
            <div className="detail-block">
              <span>Cliente</span>
              <strong>{pedido.clienteNombre}</strong>
              <small>{pedido.clienteRif}</small>
            </div>
            <div className="detail-block">
              <span>Vendedor</span>
              <strong>{pedido.vendedorNombre}</strong>
              <small>{pedido.origen === 'offline' ? 'Capturado sin conexión' : 'Origen web'}</small>
            </div>
            <div className="detail-block">
              <span>Fecha</span>
              <strong>{formatDate(pedido.createdAt)}</strong>
              <small>{formatTime(pedido.createdAt)}</small>
            </div>
            <div className="detail-block">
              <span>Estado</span>
              <StatusBadge
                estado={pedido.estado}
                local={pedido.local}
                syncStatus={
                  pedido.syncStatus ??
                  (pedido.origen === 'offline' && pedido.estado === 'en_cola'
                    ? 'synced'
                    : undefined)
                }
              />
              <small>{pedido.local ? 'Pendiente de sincronizar' : 'Guardado en el servidor'}</small>
            </div>
          </div>

          {pedido.local ? (
            <div className="inline-note">
              Este pedido vive en IndexedDB. Se enviará solo cuando haya conexión y el
              backend confirme la clave de idempotencia.
            </div>
          ) : null}

          {items.length > 0 ? (
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    <th scope="col">Producto</th>
                    <th scope="col" className="align-right">Cantidad</th>
                    <th scope="col" className="align-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id ?? `${item.sku}-${item.nombre}`}>
                      <td>
                        <div className="doc-cell">
                          <strong>{item.nombre}</strong>
                          <span>{item.sku}</span>
                        </div>
                      </td>
                      <td className="align-right">{item.cantidad}</td>
                      <td className="align-right">{formatMoney(item.totalUsd * pedido.tasaAplicada, 'VES')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <dl className="detail-total">
            <div>
              <dt>Total Bs.</dt>
              <dd>
                {pedido.moneda === 'VES'
                  ? formatMoney(pedido.totalMoneda, 'VES')
                  : displayMoney(pedido.totalUsd)}
              </dd>
            </div>
          </dl>
        </div>

        <footer className="modal-card__foot">
          <button type="button" className="btn btn--outline" onClick={onClose}>
            Cerrar
          </button>
          {next && !pedido.local ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void advance()}
              disabled={change.isPending}
            >
              {change.isPending ? 'Guardando…' : next.label}
            </button>
          ) : null}
        </footer>
      </section>
    </div>,
    document.body,
  );
}
